/*
Copyright (c) NAVER Corp.
name: @egjs/react-infinitegrid
license: MIT
author: NAVER Corp.
repository: https://github.com/naver/egjs-infinitegrid
version: 3.3.0
*/
import { createElement, Children, createRef, Component } from 'react';
import NativeInfiniteGrid, { categorize, ItemManager, INFINITEGRID_EVENTS, CONTAINER_CLASSNAME, GridLayout as GridLayout$1, withInfiniteGridMethods, JustifiedLayout as JustifiedLayout$1, FrameLayout as FrameLayout$1, SquareLayout as SquareLayout$1, PackingLayout as PackingLayout$1 } from '@egjs/infinitegrid';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

/* global Reflect, Promise */

var _extendStatics = function extendStatics(d, b) {
  _extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) {
      if (b.hasOwnProperty(p)) d[p] = b[p];
    }
  };

  return _extendStatics(d, b);
};

function __extends(d, b) {
  _extendStatics(d, b);

  function __() {
    this.constructor = d;
  }

  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __decorate(decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function LoadingBar(props) {
  return props.loading;
}

function camelize(str) {
  return str.replace(/[\s-_]([a-z])/g, function (all, letter) {
    return letter.toUpperCase();
  });
}

var InfiniteGrid = /*#__PURE__*/function (_super) {
  __extends(InfiniteGrid, _super);

  function InfiniteGrid() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.state = {
      layout: "",
      nextFunction: null
    };
    _this.wrapperRef = createRef();
    _this.containerRef = createRef();
    return _this;
  }

  var __proto = InfiniteGrid.prototype;

  __proto.render = function () {
    var props = this.props;
    var attributes = {};
    var items = this.toItems();
    var Tag = this.props.tag;

    for (var name in props) {
      if (name in InfiniteGrid.defaultProps) {
        continue;
      }

      attributes[name] = props[name];
    }

    var visibleChildren = [];
    var ig = this.ig;

    if (ig) {
      var state = this.state;
      var result = ig.beforeSync(items);
      state.layout = result === "relayout" ? result : state.layout || result;
      visibleChildren = ig.getRenderingItems().map(function (item) {
        return item.jsx;
      });
    } else {
      var groups = categorize(items);

      if (props.status) {
        var _a = props.status._infinite,
            startCursor = _a.startCursor,
            endCursor = _a.endCursor;
        visibleChildren = ItemManager.pluck(groups.slice(startCursor, endCursor + 1), "items").map(function (item) {
          return item.jsx;
        });
      } else if (props.useFirstRender) {
        visibleChildren = items.map(function (item) {
          return item.jsx;
        });
      }
    }

    if (this.props.loading) {
      visibleChildren.push(createElement(LoadingBar, {
        key: "loadingBar",
        loading: this.props.loading
      }));
    }

    return createElement(Tag, _assign({}, attributes, {
      ref: this.wrapperRef
    }), this.renderContainer(visibleChildren));
  };

  __proto.componentDidUpdate = function () {
    var ig = this.ig;
    var state = this.state;
    var layout = state.layout;
    var nextFunction = state.nextFunction;
    var elements = this.getElements();
    this.setLoadingElement();
    ig.sync(elements);

    if (layout) {
      state.layout = "";
      ig.layout(layout === "relayout");
    }

    if (nextFunction) {
      nextFunction();
      state.nextFunction = null;
    }
  };

  __proto.componentDidMount = function () {
    var _this = this;

    this.ig = new NativeInfiniteGrid(this.wrapperRef.current, _assign(_assign({}, this.props.options), {
      renderExternal: true
    })).on("render", function (_a) {
      var next = _a.next;

      _this.setState({
        nextFunction: next
      });
    });
    var ig = this.ig;
    INFINITEGRID_EVENTS.forEach(function (name) {
      var eventName = camelize("on " + name);
      ig.on(name, function (e) {
        _this.props[eventName](_assign(_assign({}, e), {
          currentTarget: _this
        }));
      });
    });
    ig.setLayout(this.props.layoutType, this.props.layoutOptions);
    this.setLoadingElement();

    if (this.props.status) {
      this.setStatus(this.props.status, true, this.getElements());
    } else {
      ig.beforeSync(this.toItems());
      ig.layout(true);
    }
  };

  __proto.componentWillUnmount = function () {
    this.ig.destroy();
  };

  __proto.setStatus = function (status, applyScrollPos, syncElements) {
    if (syncElements === void 0) {
      syncElements = this.getElements();
    }

    this.ig.setStatus(status, applyScrollPos, syncElements);
    return this;
  };

  __proto.renderContainer = function (children) {
    var props = this.props;
    var isOverflowScroll = props.options.isOverflowScroll;

    if (!isOverflowScroll) {
      return children;
    }

    var ContainerTag = props.containerTag;
    return createElement(ContainerTag, {
      className: CONTAINER_CLASSNAME,
      ref: this.containerRef
    }, children);
  };

  __proto.toItems = function () {
    var _a = this.props,
        children = _a.children,
        groupBy = _a.groupBy;
    var reactChildren = Children.toArray(children);
    return reactChildren.map(function (child, i) {
      var groupKey = groupBy(child, i);
      var itemKey = child.key;
      return {
        groupKey: groupKey,
        itemKey: itemKey,
        jsx: child
      };
    });
  };

  __proto.getElements = function () {
    var elements = [].slice.call((this.containerRef.current || this.wrapperRef.current).children);

    if (this.props.loading) {
      return elements.slice(0, -1);
    }

    return elements;
  };

  __proto.setLoadingElement = function () {
    var ig = this.ig;

    if (this.props.loading) {
      var loadingElement = (this.containerRef.current || this.wrapperRef.current).lastElementChild;

      if (loadingElement) {
        ig.setLoadingBar({
          append: loadingElement,
          prepend: loadingElement
        });
        return;
      }
    }

    ig.setLoadingBar();
  };

  InfiniteGrid.defaultProps = {
    tag: "div",
    containerTag: "div",
    layoutType: GridLayout$1,
    options: {},
    layoutOptions: {},
    useFirstRender: true,
    status: null,
    loading: null,
    groupBy: function groupBy(item, index) {
      var props = item.props;

      if ("data-groupkey" in props) {
        return props["data-groupkey"];
      } else if ("groupKey" in props) {
        return props.groupKey;
      } else {
        return "";
      }
    },
    onAppend: function onAppend() {},
    onPrepend: function onPrepend() {},
    onLayoutComplete: function onLayoutComplete() {},
    onImageError: function onImageError() {},
    onChange: function onChange() {},
    onContentError: function onContentError() {}
  };

  __decorate([withInfiniteGridMethods], InfiniteGrid.prototype, "ig", void 0);

  return InfiniteGrid;
}(Component);

var GridLayout = /*#__PURE__*/function (_super) {
  __extends(GridLayout, _super);

  function GridLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  GridLayout.defaultProps = _assign(_assign({}, InfiniteGrid.defaultProps), {
    layoutType: GridLayout$1
  });
  return GridLayout;
}(InfiniteGrid);

var JustifiedLayout = /*#__PURE__*/function (_super) {
  __extends(JustifiedLayout, _super);

  function JustifiedLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  JustifiedLayout.defaultProps = _assign(_assign({}, InfiniteGrid.defaultProps), {
    layoutType: JustifiedLayout$1
  });
  return JustifiedLayout;
}(InfiniteGrid);

var FrameLayout = /*#__PURE__*/function (_super) {
  __extends(FrameLayout, _super);

  function FrameLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  FrameLayout.defaultProps = _assign(_assign({}, InfiniteGrid.defaultProps), {
    layoutType: FrameLayout$1
  });
  return FrameLayout;
}(InfiniteGrid);

var SquareLayout = /*#__PURE__*/function (_super) {
  __extends(SquareLayout, _super);

  function SquareLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  SquareLayout.defaultProps = _assign(_assign({}, InfiniteGrid.defaultProps), {
    layoutType: SquareLayout$1
  });
  return SquareLayout;
}(InfiniteGrid);

var PackingLayout = /*#__PURE__*/function (_super) {
  __extends(PackingLayout, _super);

  function PackingLayout() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  PackingLayout.defaultProps = _assign(_assign({}, InfiniteGrid.defaultProps), {
    layoutType: PackingLayout$1
  });
  return PackingLayout;
}(InfiniteGrid);

export default InfiniteGrid;
export { FrameLayout, GridLayout, JustifiedLayout, PackingLayout, SquareLayout };